// Spec 131 (+ Spec 141 E14: a line of exactly `---` is a rule): escape every HTML character first, then convert only a fixed Markdown subset.
const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const LINK_CLASS = 'text-accent underline hover:text-accent-hover [overflow-wrap:anywhere]';

// One pass with alternation: generated markup is never scanned again (a `**` inside a URL stays untouched).
const INLINE = /\[([^[\]\n]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*\n]+)\*\*/g;

function inline(escaped: string): string {
  return escaped.replace(INLINE, (_m, label?: string, url?: string, bold?: string) =>
    url === undefined
      ? `<strong>${bold}</strong>`
      : `<a href="${url}" target="_blank" rel="noopener noreferrer" class="${LINK_CLASS}">${label}</a>`,
  );
}

type ListBlock = { tag: 'ul' | 'ol'; start: number; items: string[] };

function renderList(list: ListBlock): string {
  const cls = list.tag === 'ul' ? 'list-disc' : 'list-decimal';
  const start = list.tag === 'ol' && list.start !== 1 ? ` start="${list.start}"` : '';
  const items = list.items.map((i) => `<li>${i}</li>`).join('');
  return `<${list.tag} class="${cls} pl-5"${start}>${items}</${list.tag}>`;
}

export function renderChatMarkdown(text: string): string {
  const lines = text
    .replace(/[&<>"']/g, (c) => ESCAPES[c])
    .replace(/\r\n?/g, '\n')
    .split('\n');
  const out: string[] = [];
  let para: string[] = [];
  let list: ListBlock | null = null;

  const flushPara = () => {
    if (para.length) out.push(`<p>${para.join('<br>')}</p>`);
    para = [];
  };
  const flushList = () => {
    if (list) out.push(renderList(list));
    list = null;
  };
  const addItem = (tag: 'ul' | 'ol', start: number, content: string) => {
    flushPara();
    if (list?.tag !== tag) flushList();
    list ??= { tag, start, items: [] };
    list.items.push(inline(content));
  };

  for (const line of lines) {
    const bullet = /^[-*] (.+)$/.exec(line);
    const numbered = /^(\d+)\. (.+)$/.exec(line);
    if (bullet) {
      addItem('ul', 1, bullet[1]);
      continue;
    }
    if (numbered) {
      addItem('ol', Number(numbered[1]), numbered[2]);
      continue;
    }
    flushList();
    const heading = /^#{1,3} (.+)$/.exec(line);
    if (line === '---') {
      flushPara();
      out.push('<hr class="my-2 border-border">');
    } else if (heading) {
      flushPara();
      out.push(`<p><strong>${inline(heading[1])}</strong></p>`);
    } else if (line.trim() === '') {
      flushPara();
    } else {
      para.push(inline(line));
    }
  }
  flushPara();
  flushList();
  return out.join('');
}
